import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { User } from '../../types';
import { useTestBirthdayNotification, useTestAnniversaryNotification } from '../../hooks';
import {
  Send,
  PartyPopper,
  Cake,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

interface TestNotificationsCardProps {
  user: User;
}

export const TestNotificationsCard: React.FC<TestNotificationsCardProps> = ({ user }) => {
  const { t } = useTranslation('admin');
  const [testSuccess, setTestSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [anniversaryYears, setAnniversaryYears] = useState('1');
  const testBirthday = useTestBirthdayNotification();
  const testAnniversary = useTestAnniversaryNotification();

  const handleTestBirthday = async () => {
    try {
      setError(null);
      setTestSuccess(null);

      await testBirthday.mutateAsync({
        userName: user.name,
        userId: user.id,
        userPhotoUrl: user.profilePictureUrl || user.photoURL,
      });

      setTestSuccess(t('testNotifications.birthdaySuccess'));
    } catch (err: any) {
      setError(err.response?.data?.message || t('testNotifications.birthdayError'));
    }
  };

  const handleTestAnniversary = async () => {
    const years = parseInt(anniversaryYears);
    if (isNaN(years) || years < 1) {
      setError(t('testNotifications.invalidYears'));
      return;
    }

    try {
      setError(null);
      setTestSuccess(null);

      await testAnniversary.mutateAsync({
        userName: user.name,
        years: years,
        userId: user.id,
        userPhotoUrl: user.profilePictureUrl || user.photoURL,
      });

      setTestSuccess(t('testNotifications.anniversarySuccess', { years }));
    } catch (err: any) {
      setError(err.response?.data?.message || t('testNotifications.anniversaryError'));
    }
  };

  return (
    <Card data-testid="test-notifications-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          <CardTitle>{t('testNotifications.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('testNotifications.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Success/Error Messages */}
        {testSuccess && (
          <Alert data-testid="test-notifications-success-alert">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>{testSuccess}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" data-testid="test-notifications-error-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Birthday Test */}
        <div className="border rounded-lg p-4 space-y-3" data-testid="test-notifications-birthday-section">
          <div className="flex items-center gap-2">
            <Cake className="h-5 w-5 text-pink-500" />
            <h3 className="font-semibold">{t('testNotifications.birthday.title')}</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('testNotifications.birthday.description')}
          </p>
          <Button
            onClick={handleTestBirthday}
            disabled={testBirthday.isPending || testAnniversary.isPending}
            variant="outline"
            className="gap-2"
            data-testid="test-notifications-button-birthday"
          >
            {testBirthday.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('testNotifications.sending')}
              </>
            ) : (
              <>
                <Cake className="h-4 w-4" />
                {t('testNotifications.birthday.button')}
              </>
            )}
          </Button>
        </div>

        {/* Anniversary Test */}
        <div className="border rounded-lg p-4 space-y-3" data-testid="test-notifications-anniversary-section">
          <div className="flex items-center gap-2">
            <PartyPopper className="h-5 w-5 text-purple-500" />
            <h3 className="font-semibold">{t('testNotifications.anniversary.title')}</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('testNotifications.anniversary.description')}
          </p>
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-xs space-y-2">
              <Label htmlFor="years">{t('testNotifications.anniversary.years')}</Label>
              <Input
                id="years"
                type="number"
                min="1"
                value={anniversaryYears}
                onChange={(e) => setAnniversaryYears(e.target.value)}
                placeholder="1"
                data-testid="test-notifications-input-years"
              />
            </div>
            <Button
              onClick={handleTestAnniversary}
              disabled={testBirthday.isPending || testAnniversary.isPending}
              variant="outline"
              className="gap-2"
              data-testid="test-notifications-button-anniversary"
            >
              {testAnniversary.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('testNotifications.sending')}
                </>
              ) : (
                <>
                  <PartyPopper className="h-4 w-4" />
                  {t('testNotifications.anniversary.button')}
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
